/* eslint-disable no-underscore-dangle */
import axios, { CreateAxiosDefaults } from "axios"
import { useAuthStore } from "@/feature/auth/store/authStore"
import { posthogClient } from "./posthog"

const baseConfig: CreateAxiosDefaults = {
  baseURL: `${import.meta.env.VITE_API_URL}`,
}
// Test per deplou

export const axiosInstance = axios.create(baseConfig)

axiosInstance.interceptors.request.use(
  function (config) {
    const accessToken = useAuthStore.getState().session?.access_token

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)
/**
 * Set the authorization token for the axios instance
 * @param token - The access token
 */
export const setAuthToken = (token: string) => {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`
}

export const setRefreshToken = (token: string) => {
  useAuthStore.setState({
    session: {
      ...useAuthStore.getState().session!,
      refresh_token: token,
    },
  })
}

export const getRefreshToken = () =>
  useAuthStore.getState().session?.refresh_token ??
  window.localStorage.getItem(`refresh_token`)

export const removeAuthTokens = () => {
  window.localStorage.removeItem(`access_token`)
  window.localStorage.removeItem(`refresh_token`)
  useAuthStore.setState({
    session: null,
    user: null,
  })
}

type RefreshTokenResponse = {
  access_token: string
  refresh_token: string
  expires_in: number
  refresh_expires_in: number
}

const handleRefreshToken = async (refresh_t: string) => {
  const response = await axiosInstance.post<RefreshTokenResponse>(
    `/auth/refresh-token`,
    {
      refresh_token: refresh_t,
    }
  )
  useAuthStore.setState({
    session: {
      ...useAuthStore.getState().session!,
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      refresh_expires_in: response.data.refresh_expires_in,
    },
  })
  return response.data
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    let refreshToken: string | null = null
    try {
      refreshToken = getRefreshToken() as string
    } catch (_e) {
      return Promise.reject(_e)
    }
    if (!refreshToken) {
      return Promise.reject(error)
    }

    if (
      error.response?.status === 401 &&
      originalRequest.url.includes(`/auth/refresh-token`)
    ) {
      removeAuthTokens()
      window.location.href = `/auth/logout`
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const { access_token, refresh_token } =
          await handleRefreshToken(refreshToken)
        setAuthToken(access_token)
        setRefreshToken(refresh_token)
        return axiosInstance(originalRequest)
      } catch (refreshError) {
        // Clear auth tokens
        removeAuthTokens()
        // Redirect to login page
        window.location.href = `/auth/logout`
        return Promise.reject(refreshError)
      }
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config
    if (error.response?.status !== 401 && error.response?.status !== 403) {
      if (posthogClient?.__loaded) {
        if (posthogClient.config.capture_exceptions) {
          if (originalRequest && !originalRequest._retry) {
            posthogClient.captureException(error, {
              extra: {
                url: originalRequest.url,
                method: originalRequest.method,
                status: error.response?.status,
              },
            })
          }
        } else if (import.meta.env.DEV)
          console.warn(`Capture exceptions is disabled`)
      } else {
        console.warn(`PostHog is not loaded`)
      }
      originalRequest._retry = true
    }
    return Promise.reject(error)
  }
)
